import React, { Component } from 'react';
import PropTypes from 'prop-types';
import FormItemContainer from '../../Global/Containers/FormItemContainer';
import { withNamespaces } from 'react-i18next';
import { withApollo } from 'react-apollo';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import { withStyles } from '@material-ui/core/styles';
import SpacingStyles from '../../../styles/helpers/spacing';
import combineStyles from '../../../utils/combineStyles';
import TrackingItem from './TrackingItem';
import MultiEmailModal from '../../common/MultiEmailModal';
import EditModal from './EditModal';

import * as Mutations from '../../../graphQL/mutations/shipmentMutations';
import NotificationSnackbar from './../../common/NotificationSnackbar';
import { SHIPMENT_STATUS } from '../../../constants/constants';
import classnames from 'classnames';
import TypographyStyles from '../../../styles/typography';

const styles = theme => ({
  trackingContainer: {
    padding: 0
  },
  trackingItems: {
    paddingLeft: theme.spacing.unit * 3,
    paddingRight: theme.spacing.unit * 3,
    '& :last-child': {
      borderBottom: 'none'
    }
  },
  trackingMessageContainer: {
    textAlign: 'center',
    padding: theme.spacing.unit * 3,
    borderTop: '1px solid rgba(12,31,44,0.1)',
    color: 'rgba(12,31,44,0.38)'
  }
});
const notTendered = [SHIPMENT_STATUS.READY_TO_TENDER, SHIPMENT_STATUS.SAVED];
export class Tracking extends Component {
  static propTypes = {
    emailAddresses: PropTypes.array.isRequired,
    shipmentId: PropTypes.string.isRequired,
    shipmentStatusId: PropTypes.string.isRequired,
    orgId: PropTypes.number.isRequired,
    rowVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    onUpdate: PropTypes.func.isRequired,
    editable: PropTypes.bool.isRequired
  };
  state = {
    displayCreateModal: false,
    displayEditModal: false,
    errors: null,
    selectedEmail: {}
  };

  createTrackingAddresses = async addresses => {
    const { client: ApolloClient, shipmentId, onUpdate } = this.props;
    const allEmailAddresses = this.props.emailAddresses;
    await addresses.forEach(async address => {
      try {
        const shipment = await ApolloClient.mutate({
          mutation: Mutations.createTrackingEmailNotification,
          variables: {
            shipmentTrackingNotificationEmail: {
              shipmentId,
              emailAddress: address
            }
          }
        });
        const {
          data: {
            shipmentTrackingNotificationEmail: { create: emailAddress }
          }
        } = shipment;
        allEmailAddresses.push(emailAddress);
        onUpdate(allEmailAddresses);
      } catch (e) {
        this.setState({ displayCreateModal: false, errors: e });
      }
    });

    this.setState({ displayCreateModal: false });
  };
  _handleResendNotificationClick = async email => {
    const { client: ApolloClient } = this.props;
    try {
      await ApolloClient.mutate({
        mutation: Mutations.triggerNotificationEmail,
        variables: {
          shipmentTrackingNotificationEmailId: email.id
        }
      });
    } catch (e) {
      this.setState({ displayCreateModal: false, errors: e });
    }
  };
  addTrackingButton = () => {
    const { classes, editable } = this.props;
    return (
      editable && (
        <IconButton
          key="add"
          aria-label="Add"
          color="primary"
          onClick={() => {
            this.toggleDisplayCreateModal(true);
          }}
          classes={{ root: classes.noPadding }}
        >
          <AddIcon />
        </IconButton>
      )
    );
  };
  onDeleteAddress = async address => {
    const { client: ApolloClient, onUpdate, emailAddresses } = this.props;

    try {
      await ApolloClient.mutate({
        mutation: Mutations.deleteTrackingEmailNotification,
        variables: {
          shipmentTrackingNotificationEmailId: address.id
        }
      });

      const updatedList = emailAddresses.filter(emailAddress => {
        return emailAddress.id !== address.id;
      });
      onUpdate(updatedList);
    } catch (e) {
      this.setState({ displayCreateModal: false, errors: e });
    }
  };
  onUpdateAddress = async address => {
    const { client: ApolloClient, onUpdate, emailAddresses } = this.props;
    try {
      await ApolloClient.mutate({
        mutation: Mutations.updateTrackingEmailNotification,
        variables: {
          shipmentTrackingNotificationEmail: address
        }
      });
      const existingEmailIndex = emailAddresses.findIndex(emailAddress => {
        return emailAddress.id === address.id;
      });
      emailAddresses[existingEmailIndex] = address;
      onUpdate(emailAddresses);
      this.toggleDisplayEditModal(false);
    } catch (e) {
      this.setState({ displayUpdateModal: false, errors: e });
    }
  };
  toggleDisplayCreateModal = display => {
    this.setState({ displayCreateModal: display });
  };
  _handleErrorOnClose = () => {
    this.setState({ errors: null });
  };
  _handleEditClick = selectedEmail => {
    this.setState({ selectedEmail });
    this.toggleDisplayEditModal(true);
  };
  toggleDisplayEditModal = display => {
    this.setState({ displayEditModal: display });
  };
  render() {
    const {
      t,
      classes,
      emailAddresses,
      shipmentStatusId,
      editable
    } = this.props;
    const { displayCreateModal, displayEditModal, selectedEmail } = this.state;
    const displayTrackingLinksMessage = notTendered.includes(shipmentStatusId);
    return (
      <FormItemContainer
        title={t('Tracking')}
        secondaryAction={this.addTrackingButton()}
        classes={{ contents: classes.trackingContainer }}
      >
        <div className={classes.trackingItems}>
          {emailAddresses.map((email, index) => {
            return (
              <TrackingItem
                key={index}
                email={email}
                shipmentStatusId={shipmentStatusId}
                onDelete={this.onDeleteAddress}
                onUpdate={this.onUpdateAddress}
                onEditClick={this._handleEditClick}
                onTriggerResendEmail={this._handleResendNotificationClick}
                editable={editable}
              />
            );
          })}
        </div>
        <MultiEmailModal
          open={displayCreateModal}
          onCancel={() => {
            this.toggleDisplayCreateModal(false);
          }}
          onSave={this.createTrackingAddresses}
          title={t('Add Tracking Recipient(s)')}
        />
        <EditModal
          open={displayEditModal}
          email={selectedEmail}
          onCancel={() => {
            this.toggleDisplayEditModal(false);
          }}
          onUpdate={this.onUpdateAddress}
        />
        <NotificationSnackbar
          open={Boolean(this.state.errors)}
          handleSnackbarClose={this._handleErrorOnClose}
          message={t('Failed to save tracking addresses')}
        />
        {displayTrackingLinksMessage && (
          <div
            className={classnames(
              classes.trackingMessageContainer,
              classes.body2
            )}
          >
            {t(
              'Tracking links will be sent once the shipment has been tendered.'
            )}
          </div>
        )}
      </FormItemContainer>
    );
  }
}

const translatedTracking = withNamespaces()(withApollo(Tracking));
export default withStyles(
  combineStyles(SpacingStyles, TypographyStyles, styles)
)(translatedTracking);
